import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { BreadcrumbComponent } from 'src/app/components/shared/breadcrumb/breadcrumb.component';
import { AuthService } from 'src/app/services/auth.service';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FirebaseStorageService } from 'src/app/services/firebase-storage.service';
import { ApiService } from 'src/app/services/api.service';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [ CommonModule, FormsModule, MatFormFieldModule,BreadcrumbComponent, MatInputModule, MatIconModule, MatButtonModule, MatProgressBarModule,  MatButtonToggleModule, MatProgressSpinnerModule ],
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent {
  expandedSection: 'profile' | 'help' | null = null;

  constructor(
    private snackBar: MatSnackBar,
    private router: Router,
    public authService: AuthService,
    private storageService: FirebaseStorageService,
    private apiService: ApiService
  ) {}

  async ngOnInit() {
    await this.loadUserData();
  }

  async loadUserData() {
    try {
      // Fetch user profile from backend API
      const response = await this.apiService.getUserProfile();
      // The backend returns { user: { ... } }
      const user = response?.user;
      if (user) {
        this.profileFormValues = {
          name: user.name || '',
          phoneNumber: user.mobile || '',
          email: user.email || '',
        };
        if (user.profile?.photoURL) {
          this.profileImageUrl = user.profile.photoURL;
        } else {
          this.profileImageUrl = null;
        }
        console.log('✅ User data loaded from backend:', this.profileFormValues);
      } else {
        console.warn('⚠️ No user data returned from backend.');
      }
    } catch (error) {
      console.error('❌ Failed to load user data from backend:', error);
    }
  }

  // ✅ Toggle section visibility
  toggleSection(section: 'profile' | 'help' | null) {
    this.expandedSection = this.expandedSection === section ? null : section;
  }

  // ✅ Profile form data
  profileFormValues = {
    name: '',
    phoneNumber: '',
    email: '',
  };

  profileSaving = false;
  profileImageUrl: string | null = null;
  uploadingImage = false;
  uploadProgress = 0;

  // 📸 Handle profile image selection and upload
  async onProfileImageSelected(event: any) {
    const file = event.target.files[0];
    if (!file || !this.authService.currentUser) return;

    // Preview selected image
    const reader = new FileReader();
    reader.onload = () => (this.profileImageUrl = reader.result as string);
    reader.readAsDataURL(file);

    try {
      this.uploadingImage = true;
      this.uploadProgress = 0;
      const userId = this.authService.currentUser.uid || this.authService.currentUser.id;
      // Upload to Firebase Storage
      const downloadUrl = await this.storageService.uploadFile(
        `users/${userId}/profile`,
        file,
        (percent) => (this.uploadProgress = percent)
      );
      this.uploadingImage = false;
      this.profileImageUrl = downloadUrl;
      this.snackBar.open('✅ Profile image uploaded!', '', { duration: 2000 });

      // Automatically update profile with new photoURL
      await this.updateProfilePhotoOnly(downloadUrl);

    } catch (error: any) {
      this.uploadingImage = false;
      this.snackBar.open('❌ Failed to upload image', '', { duration: 2000 });
      console.error('❌ Failed to upload image:', error);
    }
  }

  // 🚀 Update only the profile photoURL after image upload
  async updateProfilePhotoOnly(photoURL: string) {
    try {
      const payload = {
        //name: this.profileFormValues.name,
        //email: this.profileFormValues.email,
        profile: {
          photoURL: photoURL,
        },
      };
      await this.apiService.updateUserProfile(payload);
      this.snackBar.open('✅ Profile photo updated!', '', { duration: 2000 });
      await this.loadUserData();
    } catch (error: any) {
      this.snackBar.open('❌ Failed to update profile photo', '', { duration: 2000 });
      console.error('❌ Failed to update profile photo:', error);
    }
  }

  // 💾 Save profile data (details + image)
  async saveProfile() {
    this.profileSaving = true;
    try {
      const payload = {
        name: this.profileFormValues.name,
        email: this.profileFormValues.email,
      };
      await this.apiService.updateUserProfile(payload);
      this.snackBar.open('✅ Profile updated successfully!', '', { duration: 2000 });
      await this.loadUserData();
    } catch (error: any) {
      this.snackBar.open('❌ Failed to update profile', '', { duration: 2000 });
      console.error('❌ Failed to update profile:', error);
    } finally {
      this.profileSaving = false;
    }
  }

  // 🔗 Navigate to Help page (optional)
  goToHelp() {
    this.router.navigate(['/help']);
  }
  goTobookmarks() {
    this.router.navigate(['/bookmarks']);
  }
  goTosettings() {
    this.router.navigate(['/app-settings']);
  }
  logout() {
    this.authService.signOut();
  }
}


